/**
 * The numbers behind the Agents glance strip, from the same lanes the timeline draws: what runs
 * now, how the workflows are getting on, how busy the window was, and how recent runs ended.
 * Pure, so every figure is tested.
 */
import type { Ts } from '@orgus/protocol';
import type { RunOutcome } from '@orgus/ui';
import { unitsOf, workflowProgress } from './model.ts';
import type { Lane, TimelineRun } from './model.ts';

export interface AgentsGlance {
  running: number;
  done: number;
  failed: number;
  stopped: number;
  /** Subagents and workflow agents in the window. */
  total: number;
  sessions: number;
  /** Sessions with a subagent or workflow agent running now. */
  sessionsRunning: number;
  workflowsRunning: number;
  /** Average share of phases done across running workflows, 0 to 1 (0 when none runs). */
  workflowShare: number;
  phasesDone: number;
  phases: number;
  /** Starts per equal slice of the window, oldest first. */
  starts: number[];
  /** Finished runs in the window by end time, oldest first, then the ones running. */
  outcomes: RunOutcome[];
  /** When the newest failure ended, or null. */
  lastFailedAt: Ts | null;
}

const OUTCOME: Record<TimelineRun['status'], RunOutcome> = { done: 'passed', failed: 'failed', stopped: 'cancelled', running: 'running' };

export function agentsGlance(lanes: readonly Lane[], start: Ts, now: Ts, buckets = 12): AgentsGlance {
  const units = lanes.flatMap((l) => l.runs.flatMap(unitsOf));
  const g: AgentsGlance = {
    running: 0,
    done: 0,
    failed: 0,
    stopped: 0,
    total: units.length,
    sessions: lanes.length,
    sessionsRunning: lanes.filter((l) => l.running > 0).length,
    workflowsRunning: 0,
    workflowShare: 0,
    phasesDone: 0,
    phases: 0,
    starts: Array.from({ length: buckets }, () => 0),
    outcomes: [],
    lastFailedAt: null,
  };
  const size = (now - start) / buckets;
  for (const u of units) {
    g[u.status]++;
    if (u.startedAt > start && u.startedAt <= now) g.starts[Math.min(buckets - 1, Math.floor((u.startedAt - start) / size))]! += 1;
    if (u.status === 'failed' && u.endedAt != null) g.lastFailedAt = Math.max(g.lastFailedAt ?? 0, u.endedAt);
  }
  const finished = units.filter((u) => u.status !== 'running' && u.endedAt != null).sort((a, b) => a.endedAt! - b.endedAt!);
  g.outcomes = [...finished.map((u) => OUTCOME[u.status]), ...units.filter((u) => u.status === 'running').map((): RunOutcome => 'running')];
  const workflows = lanes.flatMap((l) => l.runs).filter((r) => r.kind === 'workflow' && r.status === 'running');
  g.workflowsRunning = workflows.length;
  let share = 0;
  for (const w of workflows) {
    const phases = w.phases ?? [];
    share += workflowProgress(phases);
    g.phases += phases.length;
    g.phasesDone += phases.filter((p) => p.agents > 0 && p.done >= p.agents).length;
  }
  g.workflowShare = workflows.length ? share / workflows.length : 0;
  return g;
}
